<template>
  <div>
    <LoadingScreen v-if="!renderPresentationDetail" />
    <PresentationEditor v-if="currentUser.id">
      <template #SocketError>
        <SocketError />
      </template>
      <template #WarningParticipantLimit>
        <WarningParticipantLimit />
      </template>
      <template #SignUpCTAHeader v-if="currentUser.isGuest">
        <SignUpCTAHeader />
      </template>
      <template #EditorHeaderWrapper>
        <EditorHeaderWrapper />
      </template>
      <template #PublicAnnouncement>
        <PublicAnnouncement :z-index="150" />
      </template>

      <template #UserInactiveReloadModal v-if="showPopupUserInactive">
        <UserInactiveReloadModal />
      </template>

      <template #SideLeft v-if="showSideLeft">
        <SideLeft />
      </template>

      <template #SlideSearchPopup v-if="isShowingSlideSearchPopup">
        <SlideSearchPopup
          :closeSlideSearchPopup="closeSlideSearchPopup"
        />
      </template>
      <template #AIReachRequestLimit v-if="isAIRequestLimitModalVisible">
        <AIReachRequestLimit v-aha-emit-action.view />
      </template>
      <template #Middle>
        <Middle
          class="aha-middle"
          :class="{
            'editor-mode': !isBackstage,
            'showing-popup-search': isShowingSlideSearchPopup
          }"
        />
      </template>
      <template #RightPanelContainer>
        <template v-if="canUseRefactorRightNav">
          <PanelContainer />
        </template>
        <template v-else>
          <template v-if="isRightPanelContainerVisible">
            <template v-if="canUseAIChatbot">
              <AIAutoPanel
                v-show="isAIAutoPanelVisible"
              />
              <RightPanelContainer
                v-if="!isAIAutoPanelVisible"
                :showingAudioTab="showingAudioTab"
                :key="slideActive.id"
              />
            </template>
            <template v-else>
              <AIAutoPanel
                v-if="isAIAutoPanelVisible"
                :key="slideActive.id"
              />
              <RightPanelContainer
                v-if="!isAIAutoPanelVisible"
                :showingAudioTab="showingAudioTab"
                :key="slideActive.id"
              />
            </template>
          </template>
          <SpellcheckerPanel v-if="isSpellcheckerPanelVisible" />
        </template>
      </template>
      <template #RightNav v-if="!isPresenting && !isBackstage && !canUseRefactorRightNav">
        <RightNavV2
          v-if="canUseTemplateLibraryInEditor"
          :oneInSelectedSlidesHasAudio="showingAudioTab"
          name="ai-panel-tooltip-trigger"
        />
        <RightNav
          v-else
          :oneInSelectedSlidesHasAudio="showingAudioTab"
          name="ai-panel-tooltip-trigger"
        />
      </template>

      <template #AIDocToSlideResultModal v-if="isDocToSlideResultModalVisible">
        <AIDocToSlideResultModal
          :isDoneGenerating="isDocToSlideDoneGenerating"
          :docToSlideResponse="docToSlideResponse"
          :isSubmittingFeedback="isDocToSlideSubmittingFeedback"
          :docToSlideContentLanguage="docToSlideContentLanguage"
          :isResponseWithError="isDocToSlideResponseWithError"
          :responseError="docToSlideResponseError"
          :customAIStumpedText="docToSlideCustomAIStumpedText"
          :isLoadingSpinnerHidden="isDocToSlideLoadingSpinnerHidden"
          :loadingProgressBar="loadingProgressBar"
          @clickOnFeedbackButton="clickOnFeedbackButton"
          @clickOnCloseDocToSlideResultModal="clickOnCloseDocToSlideResultModal"
          @clickOnAddQuizToPresentation="clickOnAddQuizToPresentation"
          v-aha-emit-action.view
        />
      </template>

      <template #TemplateLibraryModal v-if="isShowModalTemplateLibrary">
        <TemplateLibraryModal
          v-aha-emit-action.view
          :name="canUseReleaseNewDiscoverTemplates ? 'template-library-modal-v2' : 'template-library-modal'"
        />
      </template>
    </PresentationEditor>
  </div>
</template>

<script>
import moment from 'moment'

import { mapState, mapActions, mapGetters } from 'vuex'
import workspace from '@/constant/workspace'
import Config from '@/config'
import ImportStatusConstant from '@/constant/importStatus'
import constants from '@/constant'

// components
import LoadingScreen from '@/components-v2/common/loading/LoadingScreen.vue'
import PresentationEditorPPT from '@/components-v2/presentation-editor/PresentationEditorPPT.vue'
import PresentationEditor from '@/components-v2/presentation-editor/PresentationEditor.vue'
import WarningParticipantLimit from '@/components/UI/WarningParticipantLimit.vue'
import SignUpCTAHeader from './demo-mode/SignUpCTAHeader.vue'
import SocketError from '@/components/UI/SocketError'
import EditorHeaderWrapper from '@/components-v2/presentation-editor/editor-header/EditorHeaderWrapper.vue'
import EditorHeaderWrapperPPT from '@/components-v2/presentation-editor/editor-header/EditorHeaderWrapperPPT.vue'
import PublicAnnouncement from '@/components-v2/common/global-notification/PublicAnnouncement.vue'
import UserInactiveReloadModal from '@/components-v2/presentation-editor/collaboration/UserInactiveReloadModal'
import SideLeft from '@/components-v2/presentation-editor/side-left/SideLeft.vue'
import SlideSearchPopup from '@/components-v2/presentation-editor/new-slide/SlideSearchPopup.vue'
import AIReachRequestLimit from '@/components-v2/modals/AIReachRequestLimit.vue'
import Middle from '@/components/presentations/Middle'
import RightPanelContainer from '@/components-v2/presentation-editor/side-right/right-nav/RightPanelContainer.vue'
import AIAutoPanel from '@/components-v2/presentation-editor/side-right/right-nav/AIAutoPanel.vue'
import RightNav from '@/components-v2/presentation-editor/side-right/right-nav/RightNav.vue'
import RightNavV2 from '@/components-v2/presentation-editor/side-right/right-nav/RightNav.v2.vue'
import PanelContainer from '@/components-v2/presentation-editor/side-right/PanelContainer.vue'
import SpellcheckerPanel from '@/components-v2/presentation-editor/editor-header/quality-control/SpellcheckerPanel.vue'

import EventBus from '@/eventBus'

// components
import AIDocToSlideResultModal from '@/components-v2/modals/AIDocToSlideResultModal.vue'
import TemplateLibraryModal from '@/components-v2/template-library/v2/TemplateLibraryModal.vue'

const noAudioTypes = new Set(constants.slideTypesNoAudio)

export default {
  components: {
    LoadingScreen,
    PresentationEditor: Config.isEnableAddinPowerpoint ? PresentationEditorPPT : PresentationEditor,
    EditorHeaderWrapper: Config.isEnableAddinPowerpoint ? EditorHeaderWrapperPPT : EditorHeaderWrapper,
    SocketError,
    WarningParticipantLimit,
    SignUpCTAHeader,
    PublicAnnouncement,
    UserInactiveReloadModal,
    SideLeft,
    SlideSearchPopup,
    AIReachRequestLimit,
    Middle,
    RightPanelContainer,
    AIAutoPanel,
    RightNav,
    RightNavV2,
    AIDocToSlideResultModal,
    TemplateLibraryModal,
    PanelContainer,
    SpellcheckerPanel,
  },
  data() {
    return {
      timeShowPopupInactive: Config.env === 'local' ? 1 : 3,
      joinedAt: new Date(),
    }
  },
  computed: {
    ...mapState({
      currentUser: (state) => state.currentUser,
      renderPresentationDetail: (state) => state.renderPresentationDetail,
      userInactiveReload: (state) => state.userInactiveReload,
      isEnableAddinPowerpoint: (state) => state.isEnableAddinPowerpoint,
      isShowingSlideSearchPopup: (state) => state.isShowingSlideSearchPopup,
      isAIRequestLimitModalVisible: (state) => state.isAIRequestLimitModalVisible,
      docToQuizResultModalVisible: (state) => state.docToQuizResultModalVisible,
      generateFromDocImportStatus: (state) => state.generateFromDocImportStatus,
      isDocToQuizGeneratingBoxVisible: (state) => state.isDocToQuizGeneratingBoxVisible,
      docToQuizUniqueRequestId: (state) => state.docToQuizUniqueRequestId,
      presentation: (state) => state.presentation,
      importStatus: (state) => state.importStatus,
      slideActive: (state) => state.slideActive,
      isShowModalTemplateLibrary: (state) => state.isShowModalTemplateLibrary,
    }),
    ...mapGetters(['isPresenting', 'isBackstage', 'selectedSlides']),
    ...mapGetters('zoomApp', ['isZoomReady']),
    ...mapGetters('rightNav', ['rightNavTabNames', 'rightNavActiveTab', 'isAITabActive', 'isPanelActive']),
    ...mapGetters('progressBar', ['loadingProgressBar']),
    ...mapGetters('docToSlideGenerator', [
      'isDocToSlideResultModalVisible',
      'isDocToSlideDoneGenerating',
      'docToSlideResponse',
      'docToSlideContentLanguage',
      'isDocToSlideSubmittingFeedback',
      'isDocToSlideResponseWithError',
      'docToSlideResponseError',
      'docToSlideCustomAIStumpedText',
      'isDocToSlideLoadingSpinnerHidden',
      'isDocToSlideImportModalVisible',
    ]),
    ...mapGetters('takeOver', ['isTakeOverToolbarVisible']),
    ...mapGetters('featureToggles', ['canUseTemplateLibraryInEditor', 'canUseSpellchecker']),
    ...mapGetters('featureToggles', ['canUseAIImprovingSlideContent', 'canUseRefactorRightNav', 'canUseReleaseNewDiscoverTemplates']),
    ...mapGetters('aiChatbot', ['canUseAIChatbot']),

    showPopupUserInactive() {
      return this.currentUser.id && !this.isPresenting &&
        this.currentUser.accountRole !== workspace.VIEWER &&
        this.userInactiveReload
      && moment().add(-this.timeShowPopupInactive, 'minutes').diff(moment(this.joinedAt)) >= 0
    },
    showSideLeft() {
      if (this.isEnableAddinPowerpoint && this.isPresenting) return false
      return true
    },
    isUploadingOrConvertingDocToQuiz() {
      const presentationId = this.docToQuizUniqueRequestId && Number(this.docToQuizUniqueRequestId.split('-')[1])
      const status = [ImportStatusConstant.UPLOADING, ImportStatusConstant.CONVERTING]
      return status.includes(this.generateFromDocImportStatus) &&
          this.presentation.id === presentationId
    },
    isRightPanelContainerVisible() {
      if (this.isEnableAddinPowerpoint && this.isPresenting) return false
      return this.currentUser.id && !this.isBackstage && this.rightNavActiveTab
    },
    showingAudioTab() {
      return this.$store.getters.selectedSlides.some(
        (s) => s.id && !noAudioTypes.has(s.type),
      )
    },

    isAIAutoPanelVisible() {
      if (this.canUseAIImprovingSlideContent && this.isAITabActive) return true
      return false
    },

    isSpellcheckerPanelVisible() {
      return this.canUseSpellchecker && this.isPanelActive(this.rightNavTabNames.SPELL_CHECKER)
    },
  },
  created() {
    this.$store.state.isDocToQuizGeneratingBoxVisible = false
    this.$store.state.renderPresentationDetail = false
  },
  destroyed() {
    this.$store.state.renderPresentationDetail = false

    this.unsetPresentationState()
    if (document.body.classList.contains('no-scroll-template')) {
      document.body.classList.remove('no-scroll-template')
    }
  },

  methods: {
    ...mapActions([
      'getCurrentUserAction',
      'getCurrentTeamAction',
      'patchPresentationAction',
      'setErrorOnInternalServerAction',
      'updateSlideActiveAction',
      'updateSlideByIdAction',
      'patchSlideByIdAction',
      'checkSlideMovingDirectionAction',
      'undoAction',
      'redoAction',
      'clearHistory',
      'getLimitationPresentationAction',
      'updateCurrentUserAction',
    ]),
    ...mapActions('presentationReport', ['clearPresentationReportAction']),
    ...mapActions('presentationChat', ['audienceChangeStatusAction']),
    ...mapActions('contentSlideTemplates', ['clearPublicSlideTemplateAction']),
    ...mapActions('zoomApp', [
      'configZoomSdkAction',
    ]),
    ...mapActions('rightNav', ['setRightNavActiveTabAction', 'showSlideTypeSelectionAction']),
    ...mapActions('slideInsertionToPresentation', ['addSlideToPresentationAction']),
    ...mapActions('docToSlideGenerator', [
      'handleDocToSlideErrorAction',
      'toggleDocToSlideResultModalAction',
      'setDocToSlideResponseAction',
      'docToSlideGenerateSlidesFromFeedbackAction',
      'generateDocToSlideUniqueRequestIdAction',
      'setDocToSlideImportStageAction',
      'setDocToSlideResponseErrorAction',
      'toggleDocToSlideImportModalAction',
    ]),
    async unsetPresentationState() {
      // TECH DEBT: setSlideActiveAction must be called before setPresentationAction because
      // we handle the action the wrong way in the store (use the presentation data to mutate the slide data)
      // Check setSlideActiveAction for more context
      // await this.setSlideActiveAction({})
      // this.setPresentationAction({})
    },
    closeSlideSearchPopup() {
      this.$store.state.isShowingSlideSearchPopup = false
      this.patchPresentationAction({
        isShowingSlideSearch: false,
      })
    },

    clickOnCloseDocToSlideImportModal() {
      this.setDocToSlideResponseErrorAction(null)
      this.toggleDocToSlideImportModalAction(false)
    },
    clickOnFeedbackButton(feedback) {
      this.docToSlideGenerateSlidesFromFeedbackAction(feedback)
    },
    clickOnCloseDocToSlideResultModal() {
      EventBus.$emit('doc-to-quiz: close_generating_toast')

      this.generateDocToSlideUniqueRequestIdAction(null)
      this.setDocToSlideImportStageAction(ImportStatusConstant.NO_IMPORT)
      this.setDocToSlideResponseAction([])
      this.toggleDocToSlideResultModalAction(false)
    },
    clickOnAddQuizToPresentation(response) {
      const payload = {
        response,
        type: 'docToSlideGenerator',
      }
      this.addSlideToPresentationAction(payload)
    },
  },
}
</script>

<style lang="scss" scoped>
.aha-middle {
  &.showing-popup-search {
    &::v-deep .live-demo-audience {
      display: none;
    }
  }
}
.aha-tabs {
  border-bottom: 1px solid rgb(235, 235, 235); // base-30
  @apply mb-4;
  &::v-deep .aha-tab-title {
    @apply py-2;
  }
}
</style>
